/** Plain words for the data module's states, shared by its pages. */
import type { Tone } from "../../../contract";
import { t } from "../../../web/lib/i18n";
import { registry } from "../../../web/registry";
import { Chip } from "../../../web/ui/kit";
import { buildTargets, type Target } from "../shared/targets";
import type { ColumnOutcome, ExchangeStatus, ImportStatus, RowOutcome, ShareCategory, TableFamily } from "../shared/types";

export const COLUMN_OUTCOME: Record<ColumnOutcome, { label: string; tone: Tone; icon: string }> = {
  exact: { label: "Exact", tone: "good", icon: "check" },
  translated: { label: "Translated", tone: "info", icon: "forward" },
  preserved: { label: "Kept as written", tone: "neutral", icon: "archive" },
  unsupported: { label: "Not brought in", tone: "neutral", icon: "minus" },
  unverified: { label: "Check this", tone: "warn", icon: "warn" },
};

export const ROW_OUTCOME: Record<RowOutcome, { label: string; tone: Tone }> = {
  create: { label: "New", tone: "good" }, update: { label: "Updated", tone: "info" }, unchanged: { label: "No change", tone: "neutral" },
  conflict: { label: "Differs", tone: "warn" }, duplicate: { label: "Repeated", tone: "neutral" }, blocked: { label: "Needs a look", tone: "warn" },
  skipped: { label: "Skipped", tone: "neutral" }, kept: { label: "Kept in file", tone: "neutral" },
};

export const IMPORT_STATUS: Record<ImportStatus, { label: string; tone: Tone }> = {
  review: { label: "Ready to review", tone: "info" }, committing: { label: "Filing", tone: "warn" }, committed: { label: "Filed", tone: "good" },
  undone: { label: "Undone", tone: "neutral" }, failed: { label: "Couldn't read", tone: "bad" }, discarded: { label: "Put aside", tone: "neutral" },
};

export const EXCHANGE_STATUS: Record<ExchangeStatus, { label: string; tone: Tone }> = {
  prepared: { label: "Prepared", tone: "neutral" }, sent: { label: "Sent", tone: "info" }, acknowledged: { label: "Accepted", tone: "good" },
  partial: { label: "Partly accepted", tone: "warn" }, rejected: { label: "Rejected", tone: "bad" }, unknown: { label: "Outcome unknown", tone: "warn" }, superseded: { label: "Replaced", tone: "neutral" },
};

export const FAMILY_LABEL: Record<TableFamily, string> = {
  animals: "Cattle", places: "Places", events: "Records", v1_animals: "Hondo V1 cattle list", v1_records: "Hondo V1 ranch log", v1_tasks: "Hondo V1 tasks", backup: "Hondo backup", unknown: "Not sure",
};

export const CATEGORY_LABEL: Record<ShareCategory, string> = {
  identity: "Identity", health: "Health", breeding: "Breeding", weights: "Weights", movements: "Moves", herd: "Arrivals and departures", genetics: "Genetics", dairy: "Dairy", notes: "Notes",
};

export const KIND_LABEL: Record<string, string> = { association: "Association", lab: "Lab", sensor: "Sensor or device", feedlot: "Feedlot or buyer", program: "Program", other: "Other" };

/** Sources Hondo names itself read in the person's language; names people typed stay as written. */
export function sourceName(name: string): string {
  if (name === "Spreadsheets") return t("Spreadsheets");
  const backup = /^Hondo backup: (.*)$/.exec(name);
  return backup ? t("Hondo backup: {{ranch}}", { ranch: backup[1] }) : name;
}

export function StatusChip({ map, value }: { map: Record<string, { label: string; tone: Tone }>; value: string }) {
  const s = map[value];
  return <Chip tone={s?.tone ?? "neutral"}>{t(s?.label ?? value)}</Chip>;
}

export function OutcomeChip({ outcome }: { outcome: ColumnOutcome }) {
  const o = COLUMN_OUTCOME[outcome];
  return <Chip tone={o.tone} icon={o.icon}>{t(o.label)}</Chip>;
}

export function targetLabel(target: Target | undefined, id: string): string {
  if (!target) return id;
  if (target.prefix) return `${t(target.prefix)}: ${t(target.label)}`;
  return t(target.label);
}

/** The target vocabulary for a table, the same one the server checks against. */
export function targetsFor(family: TableFamily, eventType?: string | null): Target[] {
  return buildTargets(registry, family === "v1_animals" ? "animals" : family, { eventType });
}

export const formatBytes = (n: number | null | undefined): string => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
